import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { AssistResourceBM, OrderActionRequest } from '@cue/api';
import { cueAdd, cueCheckCircle, cueClose, cueOrders, IconComponent } from '@cue/ui/icons';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ImageWidthPipe, RtlDirective } from '@cue/utilities';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { translation } from '@cue/translations';
import { ServiceSelectComponent } from '@assist/info-tabs';
import { AssistOrderGridItem, AuthService, OrdersService, ServiceSelectDataItem } from '@assist/shared/data';
import { localized } from '@cue/translate';
import { ButtonComponent } from '@cue/assist/ui/buttons';
import { LoadingComponent } from '@cue/loading';
import { NotificationService } from '@assist/notifications/services';
import { rxResource } from '@angular/core/rxjs-interop';
import { add, startOfDay } from 'date-fns';
import {
  OrderItemCardComponent,
  OrdersResourceDetailComponent,
  OrdersResourceDetailData,
} from '@assist/orders/widgets';
import { Dialog } from '@angular/cdk/dialog';
import { Router } from '@angular/router';

@Component({
  selector: 'assist-multiview-renew-requests',
  imports: [
    IconComponent,
    ReactiveFormsModule,
    RtlDirective,
    TranslocoPipe,
    ServiceSelectComponent,
    ImageWidthPipe,
    ButtonComponent,
    LoadingComponent,
    OrderItemCardComponent,
  ],
  templateUrl: './multiview-renew-requests.component.html',
  styleUrl: './multiview-renew-requests.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiviewRenewRequestsComponent {
  ICONS = {
    cueAdd,
    cueClose,
    cueOrders,
    cueCheckCircle,
  };

  orders: FormArray = new FormArray([]);
  form = new FormGroup({
    note: new FormControl(''),
    items: this.orders,
  });

  previousOrders = rxResource({
    params: () => this.resource().name,
    stream: ({ params }) =>
      this.ordersService.getOrders({
        from: startOfDay(new Date()),
        to: add(startOfDay(new Date()), { days: 10 }),
        skip: 0,
        take: 100,
        resourceNames: [params],
        services: [],
        showEventOrders: true,
        showResourceOrders: true,
      }),
  });

  translocoService = inject(TranslocoService);
  authService = inject(AuthService);
  ordersService = inject(OrdersService);
  usedServiceIds = signal<Record<number, true>>({});

  notificationsService = inject(NotificationService);
  resource = input.required<AssistResourceBM>();

  adding = signal(false);

  add() {
    this.adding.set(true);
  }

  onCancel() {
    this.adding.set(false);
  }

  loading = signal(false);

  createNewOrderItem(item: ServiceSelectDataItem) {
    const group: FormGroup<{
      serviceId: FormControl<number>;
      name: FormControl<string>;
      imageUrl: FormControl<string>;
      noteAllowed: FormControl<boolean>;
      note?: FormControl<string>;
      noteMinLength?: FormControl<number>;
    }> = new FormGroup({
      serviceId: new FormControl(item.id, { nonNullable: true }),
      name: new FormControl(localized(item.name, this.translocoService), { nonNullable: true }),
      imageUrl: new FormControl(item.imageUrl, { nonNullable: true }),
      noteAllowed: new FormControl(item.noteAllowed, { nonNullable: true }),
    });

    if (item.noteAllowed) {
      group.addControl('note', new FormControl('', { nonNullable: true }));
      if (item.noteMinLength > 0) {
        group.get('note').addValidators([Validators.required, Validators.minLength(item.noteMinLength)]);
      }
      group.addControl('noteMinLength', new FormControl(item.noteMinLength));
    }
    return group;
  }

  onSelect(item: ServiceSelectDataItem) {
    this.usedServiceIds.update((s) => ({
      ...s,
      [item.id]: true,
    }));
    this.orders.push(this.createNewOrderItem(item));
    this.adding.set(false);
  }

  remove(order: AbstractControl, index: number) {
    this.usedServiceIds.update((s) => ({
      ...s,
      [order.value.serviceId]: undefined,
    }));
    this.orders.removeAt(index);
  }

  order() {
    if (this.orders.length == 0) {
      return;
    }
    const email = this.authService.getEmail();
    if (this.form.valid) {
      this.loading.set(true);
      const value = this.form.value;
      const request: OrderActionRequest = {
        order: {
          note: value.note,
          items: value.items.map((oi) => ({
            note: oi.note,
            cancelled: false,
            serviceId: oi.serviceId,
          })),
        },
        data: {
          doneBy: 'assist',
          userEmail: email,
          resourceUsername: this.resource().username,
        },
      };
      this.ordersService.createEventLessOrder(request).subscribe(() => {
        this.notificationsService.success('Orderd', 'ordered');
        this.orders.clear();
        this.form.patchValue({
          note: '',
        });
        this.showConfirmation.set(true);
        this.previousOrders.reload();
        this.loading.set(false);
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  dialog = inject(Dialog);
  router = inject(Router);
  onDetailClicked(orderItem: AssistOrderGridItem) {
    this.dialog
      .open<boolean, OrdersResourceDetailData>(OrdersResourceDetailComponent, {
        data: {
          hideLocation: true,
          existingOrder: orderItem,
          resources: [this.resource() as any],
        },
      })
      .closed.subscribe((x) => {
        if (x == true) {
          this.router.navigate(['/orders']);
        }
      });
  }

  protected readonly translation = translation;
  showConfirmation = signal(false);
}
